import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";

import {
  fetchPatientScheduleQuestionnaires,
  submitPatientScheduleQuestionnaireOnBehalf,
} from "../../services/patientService";
import { normalizeQuestionSchemaForBackend } from "../questionnaire-management/questionnaireSchemaUtils";

import "survey-core/defaultV2.min.css";
import "../patient/patientSurveyOverrides.css";

function toErrorMessage(error, fallback) {
  const raw = error?.message || "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === "string") return parsed.detail;
  } catch {
    // ignore
  }
  return String(raw || fallback);
}

export default function PatientQuestionnaireOnBehalfDialog({
  open,
  onClose,
  patientId,
  executionId,
  scheduleId,
  scheduleLabel,
  onSubmitted,
}) {
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [surveyModel, setSurveyModel] = useState(null);

  useEffect(() => {
    if (!open || !patientId || !executionId || !scheduleId) {
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const data = await fetchPatientScheduleQuestionnaires(patientId, executionId, scheduleId);
        if (!cancelled) {
          setSteps(Array.isArray(data) ? data : []);
          setActiveStepIndex(0);
        }
      } catch (err) {
        if (!cancelled) {
          setError(toErrorMessage(err, "Failed to load questionnaires."));
          setSteps([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, patientId, executionId, scheduleId]);

  const activeStep = steps[activeStepIndex] || null;

  useEffect(() => {
    if (!open || !activeStep?.schema) {
      setSurveyModel(null);
      return undefined;
    }

    try {
      let rawSchema = activeStep.schema;
      if (typeof rawSchema === "string") {
        try {
          rawSchema = JSON.parse(rawSchema);
        } catch {
          setSurveyModel(null);
          return undefined;
        }
      }

      const normalizedSchema = normalizeQuestionSchemaForBackend(rawSchema || {});
      const model = new Model(normalizedSchema);

      if (activeStep.submitted_answers && typeof activeStep.submitted_answers === "object") {
        model.data = activeStep.submitted_answers;
      }

      const statusLower = String(activeStep.status || "").toLowerCase();
      let cleanup;

      if (statusLower === "completed") {
        model.mode = "display";
      } else {
        const onCompleteHandler = async (sender) => {
          const currentStatus = String(activeStep.status || "").toLowerCase();
          if (currentStatus === "completed") return;

          setSaving(true);
          setError("");
          setSuccessMessage("");
          try {
            const resultData = sender.data || {};
            await submitPatientScheduleQuestionnaireOnBehalf({
              patientId,
              executionId,
              scheduleId,
              qid: activeStep.qid,
              answersJson: resultData,
            });

            const currentIndex = steps.findIndex((step) => step.qid === activeStep.qid);
            let nextIndex = -1;
            for (let i = currentIndex + 1; i < steps.length; i += 1) {
              const step = steps[i];
              if (String(step.status || "").toLowerCase() !== "completed") {
                nextIndex = i;
                break;
              }
            }

            setSteps((prev) =>
              prev.map((step) =>
                step.qid === activeStep.qid
                  ? { ...step, status: "completed", submitted_answers: resultData }
                  : step
              )
            );
            setSuccessMessage("Answers saved on behalf of the subject.");
            sender.mode = "display";
            onSubmitted?.();

            if (nextIndex !== -1) {
              setActiveStepIndex(nextIndex);
            }
          } catch (err) {
            setError(toErrorMessage(err, "Failed to save answers."));
          } finally {
            setSaving(false);
          }
        };

        model.onComplete.add(onCompleteHandler);
        cleanup = () => {
          model.onComplete.remove(onCompleteHandler);
        };
      }

      setSurveyModel(model);
      return cleanup;
    } catch {
      setSurveyModel(null);
      return undefined;
    }
  }, [activeStep, executionId, onSubmitted, open, patientId, scheduleId, steps]);

  const handleStepChange = (index) => {
    if (index < 0 || index >= steps.length) return;
    setActiveStepIndex(index);
    setSuccessMessage("");
    setError("");
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Answer on behalf — {scheduleLabel || `Schedule ${scheduleId}`}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f5f8fb" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !steps.length ? (
          <Typography color="text.secondary">
            {error || "No questionnaires are configured for this visit."}
          </Typography>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are submitting questionnaires on behalf of the subject. Completed steps are read-only.
            </Alert>

            <Stepper nonLinear activeStep={activeStepIndex} alternativeLabel sx={{ mb: 2, overflowX: "auto" }}>
              {steps.map((step, index) => (
                <Step key={step.qid} completed={String(step.status).toLowerCase() === "completed"}>
                  <StepButton color="inherit" onClick={() => handleStepChange(index)}>
                    <StepLabel>
                      Step {step.step}: {step.label || step.name || step.qid}
                    </StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>

            {activeStep ? (
              <Box sx={{ p: 2, bgcolor: "#fff", borderRadius: 2, border: "1px solid #dde5ec" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Step {activeStep.step}: {activeStep.label || activeStep.name || activeStep.qid}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fill the form below and use the Complete button at the end of the survey.
                </Typography>

                {surveyModel ? (
                  <Box className="patient-survey-runtime">
                    <Survey model={surveyModel} />
                  </Box>
                ) : (
                  <Typography color="error">Unable to render this questionnaire.</Typography>
                )}
              </Box>
            ) : null}

            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}
            {successMessage ? (
              <Alert severity="success" sx={{ mt: 2 }}>
                {successMessage}
              </Alert>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
